'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { emitTrackInlineContactFormViewEvent } from '@/src/analytics/hotpads/events';
import { ContactFormSpoke } from '../ContactForm';
import type { ContactSubmissionContext } from '../ContactForm/types';
import { innerCard, tealFrame, wrapperRelative } from './inlineContactModule.styles';

// Interaction-only: renders after a successful contact submit; matches the
// dynamic import in useContactFormAction so the chunk stays out of the eager bundle.
const PostContactFlowModal = dynamic(() =>
  import('../ContactForm/PostContactFlowModal').then((mod) => mod.PostContactFlowModal),
);

type Props = {
  listing: PropertyDetailListing;
};

export const InlineContactModule = ({ listing }: Props) => {
  const [dismissedAfterSuccess, setDismissedAfterSuccess] = useState(false);
  const [postContactContext, setPostContactContext] = useState<ContactSubmissionContext | null>(null);
  const impressionTargetRef = useRef<HTMLDivElement>(null);
  const trackedListingAliasRef = useRef<string | null>(null);

  useEffect(() => {
    const target = impressionTargetRef.current;
    if (
      trackedListingAliasRef.current === listing.aliasEncoded ||
      !target ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (trackedListingAliasRef.current === listing.aliasEncoded || !entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      trackedListingAliasRef.current = listing.aliasEncoded;
      observer.disconnect();
      emitTrackInlineContactFormViewEvent({ listing });
    });

    observer.observe(target);

    return () => observer.disconnect();
  }, [listing]);

  if (!listing || listing.active === false || dismissedAfterSuccess) {
    return null;
  }

  return (
    <div ref={impressionTargetRef} className={wrapperRelative}>
      <div className={tealFrame}>
        <div className={innerCard}>
          <ContactFormSpoke
            listing={listing}
            mode="contact"
            onClose={() => setDismissedAfterSuccess(true)}
            onSubmitSuccess={setPostContactContext}
            idPrefix="halo-inline-contact"
            formMode="inline"
          />
        </div>
      </div>
      <PostContactFlowModal
        context={postContactContext}
        onDone={() => {
          setPostContactContext(null);
          setDismissedAfterSuccess(true);
        }}
      />
    </div>
  );
};
